import { useEffect, useRef, useState } from 'react';
import { env } from '@/utils/env';
import styles from './SocialSignIn.module.css';
import type { SocialProvider } from '@/services/api';

interface GoogleId {
  initialize(opts: {
    client_id: string;
    callback: (r: { credential: string }) => void;
    ux_mode?: 'popup';
  }): void;
  renderButton(el: HTMLElement, opts: Record<string, string | number>): void;
}
interface AppleIdAuth {
  init(opts: { clientId: string; scope: string; redirectURI: string; usePopup: boolean }): void;
  signIn(): Promise<{
    authorization: { id_token: string };
    user?: { name?: { firstName?: string; lastName?: string } };
  }>;
}
declare global {
  interface Window {
    google?: { accounts: { id: GoogleId } };
    AppleID?: { auth: AppleIdAuth };
  }
}

const GOOGLE_SDK = 'https://accounts.google.com/gsi/client';
const APPLE_SDK =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

const loaded = new Map<string, Promise<void>>();
function loadScript(src: string): Promise<void> {
  let p = loaded.get(src);
  if (!p) {
    p = new Promise<void>((resolve, reject) => {
      const el = document.createElement('script');
      el.src = src;
      el.async = true;
      el.onload = () => {
        resolve();
      };
      el.onerror = () => {
        loaded.delete(src);
        reject(new Error(`Could not load ${src}`));
      };
      document.head.appendChild(el);
    });
    loaded.set(src, p);
  }
  return p;
}

export const socialSignInEnabled = Boolean(env.googleClientId ?? env.appleClientId);

// The provider's own SDK signs the person in and hands us an ID token; the API verifies it and
// starts a normal session. Buttons appear only for providers with a configured client id.
export function SocialSignIn({
  mode,
  onToken,
}: {
  mode: 'login' | 'signup';
  onToken: (provider: SocialProvider, idToken: string, displayName?: string) => Promise<void>;
}) {
  const googleEl = useRef<HTMLDivElement>(null);
  const onTokenRef = useRef(onToken);
  useEffect(() => {
    onTokenRef.current = onToken;
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const clientId = env.googleClientId;
    if (!clientId) return;
    let cancelled = false;
    loadScript(GOOGLE_SDK).then(
      () => {
        const id = window.google?.accounts.id;
        if (cancelled || !id || !googleEl.current) return;
        id.initialize({
          client_id: clientId,
          ux_mode: 'popup',
          callback: (r) => void onTokenRef.current('google', r.credential),
        });
        id.renderButton(googleEl.current, {
          theme: 'outline',
          size: 'large',
          shape: 'pill',
          text: mode === 'signup' ? 'signup_with' : 'continue_with',
          width: 300,
        });
      },
      () => {
        setError('Google sign-in could not be loaded.');
      },
    );
    return () => {
      cancelled = true;
    };
  }, [mode]);

  const apple = async () => {
    const clientId = env.appleClientId;
    if (!clientId) return;
    setError(null);
    try {
      await loadScript(APPLE_SDK);
      const auth = window.AppleID?.auth;
      if (!auth) throw new Error('Apple SDK missing');
      auth.init({
        clientId,
        scope: 'name email',
        redirectURI: window.location.origin,
        usePopup: true,
      });
      const res = await auth.signIn();
      // Apple shares the name only on the very first sign-in, and never inside the token.
      const n = res.user?.name;
      const name = [n?.firstName, n?.lastName].filter(Boolean).join(' ') || undefined;
      await onTokenRef.current('apple', res.authorization.id_token, name);
    } catch (err) {
      const code = (err as { error?: string } | null)?.error;
      if (code !== 'popup_closed_by_user' && code !== 'user_cancelled_authorize')
        setError('Apple sign-in did not complete. Please try again.');
    }
  };

  if (!socialSignInEnabled) return null;
  return (
    <div className={styles.wrap}>
      {env.googleClientId ? <div ref={googleEl} className={styles.google} /> : null}
      {env.appleClientId ? (
        <button type="button" className={styles.apple} onClick={() => void apple()}>
          <svg viewBox="0 0 16 16" aria-hidden="true" width="16" height="16">
            <path
              fill="currentColor"
              d="M11.18 8.5c-.02-1.6 1.3-2.37 1.36-2.41-.74-1.09-1.9-1.24-2.31-1.25-.98-.1-1.92.58-2.42.58-.5 0-1.27-.57-2.09-.55-1.07.02-2.06.62-2.61 1.58-1.12 1.94-.29 4.8.8 6.37.53.77 1.16 1.63 1.99 1.6.8-.03 1.1-.52 2.07-.52.96 0 1.24.52 2.08.5.86-.01 1.4-.78 1.93-1.55.6-.89.85-1.75.87-1.8-.02 0-1.66-.64-1.68-2.55ZM9.6 3.8c.44-.53.74-1.27.66-2.01-.64.03-1.41.43-1.87.96-.41.47-.77 1.23-.67 1.95.71.06 1.44-.36 1.88-.9Z"
            />
          </svg>
          {mode === 'signup' ? 'Sign up with Apple' : 'Continue with Apple'}
        </button>
      ) : null}
      {error ? (
        <p className={styles.error} role="alert">
          {error}
        </p>
      ) : null}
      <p className={styles.divider}>
        <span>or use your work email</span>
      </p>
    </div>
  );
}
